// Эндпоинты людей: реестр, карточка, документы, статусы, назначения и тарифы.
//
// Формы — зеркало contracts/openapi/bark-core.v1.yaml. Контракт правит только сессия ядра;
// поле, придуманное на фронте, даёт зелёную сборку и пустой экран у человека.
//
// Три особенности, из-за которых типы здесь выглядят именно так.
//
// 1. **Скрытое поле ОТСУТСТВУЕТ в ответе.** У табельщика нет ключа `birth_date` вовсе —
//    не пустое значение, а нет ключа (server/app/serializers/employees.py). Поэтому такие
//    поля описаны необязательными, а карточка проверяет наличие КЛЮЧА, а не значения:
//    пустое читается как «не заполнено», отсутствующее — как «не ваше», и это разные факты.
// 2. **Деньги — целые копейки, дробные величины — строкой.** Ставка dec(18,6) и часы
//    dec(5,2) ходят строкой: число с плавающей точкой теряет копейку на дробной ставке.
// 3. **Дубль предупреждает, а не запрещает.** `duplicate_warning` приезжает ВМЕСТЕ с уже
//    созданной карточкой. Это не ошибка сохранения, а найденные совпадения рядом со
//    ссылкой на них: у заказчика встречаются полные тёзки, решает человек.

import { request, requestFile, type FileResponse } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

// ── Статусы занятости ─────────────────────────────────────────────────────────────────────

/** contracts/enums/statuses.yaml, employee_status. Список заморожен схемой базы. */
export const EMPLOYEE_STATUSES = [
  'on_shift',
  'between_shifts',
  'reserve',
  'leave',
  'sick',
  'fired',
] as const;

export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export function isEmployeeStatus(value: string): value is EmployeeStatus {
  return (EMPLOYEE_STATUSES as readonly string[]).includes(value);
}

// ── Сотрудник ─────────────────────────────────────────────────────────────────────────────

export interface DuplicateHit {
  employee_id: string;
  display_name: string;
  /** По чему совпало: телефон или документ. Человеку это важнее самого факта совпадения. */
  matched_by: 'phone' | 'document';
}

export interface DuplicateReport {
  found: boolean;
  hits?: DuplicateHit[];
}

/**
 * Карточка человека. Поля, помеченные необязательными, могут ОТСУТСТВОВАТЬ в ответе — это
 * не «может прийти null», а «ключа может не быть»: роль без права на персональные данные
 * получает ответ без них. Разметка обязана проверять наличие ключа (`'birth_date' in employee`).
 */
export interface EmployeeOut {
  id: string;
  display_name: string;
  last_name: string;
  first_name: string;
  middle_name: string | null;
  personnel_no: string | null;
  /** Телефон в нормализованном виде: нормализует одна функция на весь проект, на сервере. */
  phone_normalized: string | null;
  phone_extra: string | null;
  city: string | null;
  status: EmployeeStatus;
  hired_at: string | null;
  fired_at: string | null;
  employment_type_id: string | null;
  employment_type_title: string | null;
  site_id: string | null;
  site_name: string | null;
  region: string | null;
  guard_category: number | null;
  has_debt: boolean;
  ticket_compensation_used: boolean;
  /** Персональные данные: у роли со скрытием ключа в ответе НЕТ. */
  birth_date?: string | null;
  /** Приезжает только вместе с только что созданной карточкой. */
  duplicate_warning?: DuplicateReport | null;
}

/** Есть ли ключ в ответе. Отсутствие ключа — «поле не ваше», null — «не заполнено». */
export function hasField<K extends string>(
  employee: EmployeeOut,
  key: K,
): employee is EmployeeOut & Record<K, unknown> {
  return Object.hasOwn(employee, key);
}

export interface EmployeeIn {
  last_name: string;
  first_name: string;
  middle_name?: string | null;
  birth_date?: string | null;
  /** В любом виде: нормализует сервер одной функцией на весь проект. */
  phone?: string | null;
  phone_extra?: string | null;
  city?: string | null;
  personnel_no?: string | null;
  employment_type_id?: string | null;
  hired_at?: string | null;
  status?: EmployeeStatus;
  /** Паспорт при заведении нужен проверке дубля; хранится зашифрованным. */
  passport_series?: string | null;
  passport_number?: string | null;
}

export interface EmployeePatch {
  last_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  birth_date?: string | null;
  phone?: string | null;
  phone_extra?: string | null;
  city?: string | null;
  personnel_no?: string | null;
  employment_type_id?: string | null;
  hired_at?: string | null;
}

/** Представление реестра по типу оформления со счётчиком (сценарий 8). */
export interface EmploymentView {
  /** null — люди без проставленного типа оформления. */
  employment_type_id: string | null;
  title: string;
  count: number;
}

export interface StatusChangeIn {
  status: EmployeeStatus;
  /** Основание: приказ, заявление. */
  basis?: string | null;
}

export interface StatusHistoryOut {
  id: string;
  status_from: EmployeeStatus | null;
  status_to: EmployeeStatus;
  changed_at: string;
  changed_by: string | null;
  basis: string | null;
}

export interface FireIn {
  fired_at: string;
  /** Причина увольнения. Сервер её ТРЕБУЕТ: без неё запись не сохранится. */
  reason: string;
}

export const DOC_TYPES = ['passport', 'snils', 'inn', 'guard_license', 'medical_check'] as const;

export type DocType = (typeof DOC_TYPES)[number];

export interface DocumentIn {
  doc_type: DocType;
  series?: string | null;
  number?: string | null;
  issued_by?: string | null;
  issued_at?: string | null;
  expires_at?: string | null;
  /** Разряд охранника: только для удостоверения, 4-6. */
  guard_category?: number | null;
}

/** Номер документа наружу не уходит: сервер отдаёт только последние четыре знака. */
export interface DocumentOut {
  id: string;
  employee_id: string;
  doc_type: string;
  number_last4: string | null;
  issued_by: string | null;
  issued_at: string | null;
  expires_at: string | null;
  guard_category: number | null;
  is_active: boolean;
}

export interface BulkStatusIn {
  employee_ids: string[];
  status: EmployeeStatus;
  basis?: string | null;
}

export interface BulkResult {
  updated: number;
  /** Строки вне области видимости: их сервер молча не тронул, и об этом надо сказать. */
  skipped: number;
}

// ── Назначения и тариф ────────────────────────────────────────────────────────────────────

/** contracts/enums/statuses.yaml, rate_mode. Ставку вводят руками ТОЛЬКО в режиме direct. */
export const RATE_MODES = ['by_period', 'direct', 'by_target'] as const;
export type RateMode = (typeof RATE_MODES)[number];

/** Предупреждение мастера: мешает, но не запрещает. Решает человек. */
export interface AssignmentWarning {
  code: 'busy_elsewhere' | 'blocking_debt' | 'no_license';
  message: string;
  hint?: string | null;
}

/** Как изменилась укомплектованность объекта от этой постановки. */
export interface StaffingDelta {
  site_id: string;
  required: number;
  assigned_before: number;
  assigned_after: number;
}

export interface AssignmentOut {
  id: string;
  employee_id: string;
  employee_name: string;
  post_id: string;
  post_name: string;
  site_id: string;
  site_name: string;
  legal_entity_id: string;
  date_from: string;
  date_to: string | null;
  rate_mode: RateMode;
  /** Копейки. */
  period_amount: number | null;
  /** Часы строкой. */
  norm_hours: string | null;
  /** Ставка строкой dec(18,6). Показывается целым рублём, руками не вводится. */
  hourly_rate: string | null;
  /** Копейки. */
  target_amount: number | null;
  warnings?: AssignmentWarning[];
  staffing?: StaffingDelta | null;
  /**
   * Действуют ли объект и пост. По ним карточка не предлагает «Продлить» там, где сервер
   * откажет. Пусто или нет поля — не знаем (старый сервер, ответ не из карточки).
   */
  site_active?: boolean | null;
  post_active?: boolean | null;
}

export interface AssignmentIn {
  employee_id: string;
  post_id: string;
  legal_entity_id: string;
  date_from: string;
  date_to?: string | null;
  rate_mode?: RateMode;
  /** Копейки, режим by_period. */
  period_amount?: number | null;
  /** Часы строкой, режим by_period. */
  norm_hours?: string | null;
  /** Ставка строкой, ЕДИНСТВЕННЫЙ режим, где её вводит человек, — direct. */
  hourly_rate?: string | null;
  /** Копейки, режим by_target. */
  target_amount?: number | null;
  /** Часы строкой, режим by_target. */
  fact_hours_for_target?: string | null;
  declared_shift_days?: 45 | 60 | null;
  is_first_shift?: boolean;
}

/**
 * Постановка ПАЧКИ людей на один пост.
 *
 * Не «много раз одиночная»: тариф здесь принадлежит посту, а не человеку, поэтому он один на
 * всю пачку. Разные ставки на одном посту заводятся потом, изменением тарифа по назначению.
 */
export interface BulkAssignmentIn {
  employee_ids: string[];
  post_id: string;
  legal_entity_id: string;
  date_from: string;
  date_to?: string | null;
  rate_mode?: RateMode;
  period_amount?: number | null;
  norm_hours?: string | null;
  hourly_rate?: string | null;
  target_amount?: number | null;
  fact_hours_for_target?: string | null;
  declared_shift_days?: 45 | 60 | null;
  is_first_shift?: boolean;
}

/** Что вышло по одному человеку из пачки. Строка есть у КАЖДОГО, включая непоставленных. */
export interface BulkAssignmentRow {
  employee_id: string;
  employee_name: string;
  assignment_id: string | null;
  warnings: AssignmentWarning[];
  /** Заполнено, когда поставить не удалось: что помешало, словами. */
  skipped_reason: string | null;
}

export interface BulkAssignmentOut {
  created: number;
  skipped: number;
  rows: BulkAssignmentRow[];
  staffing: StaffingDelta;
}

/** Перевод между объектами: прежнее назначение закрывается, новое создаётся. */
export interface TransferIn {
  post_id: string;
  date_from: string;
  /** Пусто — юрлицо остаётся прежним. */
  legal_entity_id?: string | null;
  basis?: string | null;
}

/**
 * Изменение тарифа с даты. Основание ОБЯЗАТЕЛЬНО — прямая просьба заказчика: «хорошо бы
 * иметь примечание с указанием причины, чтобы не вспоминать».
 */
export interface RateChangeIn {
  effective_from: string;
  basis: string;
  /** Пусто — режим остаётся прежним. */
  rate_mode?: RateMode | null;
  period_amount?: number | null;
  norm_hours?: string | null;
  hourly_rate?: string | null;
  target_amount?: number | null;
  fact_hours_for_target?: string | null;
}

export interface RateChangeOut {
  id: string;
  assignment_id: string;
  effective_from: string;
  rate_mode: RateMode;
  old_hourly_rate: string | null;
  new_hourly_rate: string | null;
  old_period_amount: number | null;
  new_period_amount: number | null;
  target_amount: number | null;
  /**
   * Норма часов до и после, строкой. У записей до 13.10.2026 прежней может не быть: её тогда не
   * сохраняли. Необязательно: сервер до 13.10.2026 полей не присылал.
   */
  old_norm_hours?: string | null;
  new_norm_hours?: string | null;
  changed_at: string;
  changed_by: string | null;
  basis?: string | null;
}

// ── Вызовы ────────────────────────────────────────────────────────────────────────────────

export interface EmployeesQuery {
  status?: EmployeeStatus;
  site_id?: string;
  legal_entity_id?: string;
  region?: string;
  guard_category?: number;
  has_debt?: boolean;
  employment_type_id?: string;
  /** Поиск по фамилии, имени, табельному номеру и телефону. */
  q?: string;
  limit?: number;
  offset?: number;
}

function employeesQueryString(params: EmployeesQuery): string {
  return queryString({
    status: params.status,
    site_id: params.site_id,
    legal_entity_id: params.legal_entity_id,
    region: params.region,
    guard_category: params.guard_category,
    has_debt: params.has_debt,
    employment_type_id: params.employment_type_id,
    q: params.q,
    limit: params.limit,
    offset: params.offset,
  });
}

export function listEmployees(
  params: EmployeesQuery = {},
  signal?: AbortSignal,
): Promise<ListResponse<EmployeeOut>> {
  return request<ListResponse<EmployeeOut>>(`/api/v1/employees${employeesQueryString(params)}`, {
    signal,
  });
}

export function listEmploymentViews(signal?: AbortSignal): Promise<ListResponse<EmploymentView>> {
  return request<ListResponse<EmploymentView>>('/api/v1/employees/views', { signal });
}

export interface DuplicatesQuery {
  phone?: string;
  passport_series?: string;
  passport_number?: string;
  /** Себя из выдачи исключаем: при правке карточка совпадает сама с собой. */
  exclude_id?: string;
}

export function checkDuplicates(
  params: DuplicatesQuery,
  signal?: AbortSignal,
): Promise<DuplicateReport> {
  return request<DuplicateReport>(
    `/api/v1/employees/duplicates${queryString({
      phone: params.phone,
      passport_series: params.passport_series,
      passport_number: params.passport_number,
      exclude_id: params.exclude_id,
    })}`,
    { signal },
  );
}

/** Выгрузка реестра. Файл собирает СЕРВЕР: в нём должен быть тот же срез, что на экране. */
export function exportEmployees(params: EmployeesQuery = {}): Promise<FileResponse> {
  return requestFile(
    `/api/v1/employees/export${queryString({
      status: params.status,
      site_id: params.site_id,
      employment_type_id: params.employment_type_id,
      q: params.q,
    })}`,
    {},
    'Сотрудники.xlsx',
  );
}

export function createEmployee(body: EmployeeIn, idempotencyKey?: string): Promise<EmployeeOut> {
  return request<EmployeeOut>('/api/v1/employees', { method: 'POST', body, idempotencyKey });
}

export function getEmployee(employeeId: string, signal?: AbortSignal): Promise<EmployeeOut> {
  return request<EmployeeOut>(`/api/v1/employees/${encodeURIComponent(employeeId)}`, { signal });
}

export function patchEmployee(employeeId: string, body: EmployeePatch): Promise<EmployeeOut> {
  return request<EmployeeOut>(`/api/v1/employees/${encodeURIComponent(employeeId)}`, {
    method: 'PATCH',
    body,
  });
}

export function changeEmployeeStatus(
  employeeId: string,
  body: StatusChangeIn,
): Promise<EmployeeOut> {
  return request<EmployeeOut>(`/api/v1/employees/${encodeURIComponent(employeeId)}/status`, {
    method: 'POST',
    body,
  });
}

export function listStatusHistory(
  employeeId: string,
  signal?: AbortSignal,
): Promise<ListResponse<StatusHistoryOut>> {
  return request<ListResponse<StatusHistoryOut>>(
    `/api/v1/employees/${encodeURIComponent(employeeId)}/status-history`,
    { signal },
  );
}

export function fireEmployee(employeeId: string, body: FireIn): Promise<EmployeeOut> {
  return request<EmployeeOut>(`/api/v1/employees/${encodeURIComponent(employeeId)}/fire`, {
    method: 'POST',
    body,
  });
}

export function listDocuments(
  employeeId: string,
  signal?: AbortSignal,
): Promise<ListResponse<DocumentOut>> {
  return request<ListResponse<DocumentOut>>(
    `/api/v1/employees/${encodeURIComponent(employeeId)}/documents`,
    { signal },
  );
}

export function addDocument(
  employeeId: string,
  body: DocumentIn,
  idempotencyKey?: string,
): Promise<DocumentOut> {
  return request<DocumentOut>(`/api/v1/employees/${encodeURIComponent(employeeId)}/documents`, {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

export function bulkStatus(body: BulkStatusIn): Promise<BulkResult> {
  return request<BulkResult>('/api/v1/employees/bulk-status', { method: 'POST', body });
}

export function listEmployeeAssignments(
  employeeId: string,
  signal?: AbortSignal,
): Promise<ListResponse<AssignmentOut>> {
  return request<ListResponse<AssignmentOut>>(
    `/api/v1/employees/${encodeURIComponent(employeeId)}/assignments`,
    { signal },
  );
}

export function createAssignment(
  body: AssignmentIn,
  idempotencyKey?: string,
): Promise<AssignmentOut> {
  return request<AssignmentOut>('/api/v1/assignments', {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

export function createAssignmentsBulk(
  body: BulkAssignmentIn,
  idempotencyKey?: string,
): Promise<BulkAssignmentOut> {
  return request<BulkAssignmentOut>('/api/v1/assignments/bulk', {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

/** Закрытие назначения последним днём. Тело сервер ждёт объектом с одним полем. */
export function closeAssignment(assignmentId: string, dateTo: string): Promise<AssignmentOut> {
  return request<AssignmentOut>(`/api/v1/assignments/${encodeURIComponent(assignmentId)}/close`, {
    method: 'POST',
    body: { date_to: dateTo },
  });
}

// ── Продление назначения ──────────────────────────────────────────────────────────────────
//
// 10.10.2026 заказчик понял дату «По» как «период работы» и закрыл назначения сентябрём: замер
// боя 19.10.2026 — тридцать человек «на вахте» без действующего назначения, их нет ни в табеле,
// ни в ведомости октября. Продление ставит новый последний день тому же назначению: тариф,
// история тарифа и «первая вахта» остаются, второго назначения не появляется.

/** Новый последний день; `null` — без даты окончания. Основание необязательно. */
export interface ExtendIn {
  date_to: string | null;
  basis?: string | null;
  /**
   * Человек правда работает на двух постах. Без этого занятость на ДРУГОМ посту в добавляемые
   * дни — отказ 409 с кодом `busy_elsewhere`, и ничего не записано.
   */
  confirm_busy_elsewhere?: boolean;
}

/** Код отказа «в добавляемые дни человек стоит на другом посту»: его подтверждают вторым нажатием. */
export const ЗАНЯТ_НА_ДРУГОМ_ПОСТУ = 'busy_elsewhere';

/** Продление пачкой: одна дата и одно основание на всю пачку. */
export interface BulkExtendIn {
  assignment_ids: string[];
  date_to: string | null;
  basis?: string | null;
}

/** Назначение из пачки, которое не продлено, и почему. */
export interface ExtendSkipped {
  assignment_id: string;
  /** «Фамилия Имя · Объект»: чтобы найти строку глазами. */
  label: string;
  reason: string;
}

export interface BulkExtendOut {
  extended: number;
  skipped: ExtendSkipped[];
  summary: string;
}

/**
 * Человек «на вахте» без действующего и будущего назначения и его ПОСЛЕДНЕЕ назначение.
 * Поля назначения пусты, когда назначений не было вовсе.
 */
export interface OnShiftUnassignedOut {
  employee_id: string;
  employee_name: string;
  assignment_id: string | null;
  site_id: string | null;
  site_name: string | null;
  post_id: string | null;
  post_name: string | null;
  date_from: string | null;
  date_to: string | null;
  /** Почему продлить нельзя: «назначений не было», «объект закрыт», «пост снят». */
  extend_blocked_reason: string | null;
  /** Куда идти, когда продлить нельзя. */
  next_action: string | null;
}

export interface OnShiftUnassignedListOut {
  /** «Сегодня» сервера: на этот день у людей нет ни действующего, ни будущего назначения. */
  on_date: string;
  items: OnShiftUnassignedOut[];
  total: number;
  sites: number;
  summary: string;
}

/**
 * Продлить одно назначение.
 *
 * Ключа операции нет: повтор с тем же днём сервер принимает молча и журнал не пишет, то есть
 * двойное нажатие безвредно и без ключа — в том числе когда оба запроса пришли одновременно
 * (запись условная). Занятость на другом посту — отказ `ЗАНЯТ_НА_ДРУГОМ_ПОСТУ` до подтверждения.
 */
export function extendAssignment(assignmentId: string, body: ExtendIn): Promise<AssignmentOut> {
  return request<AssignmentOut>(`/api/v1/assignments/${encodeURIComponent(assignmentId)}/extend`, {
    method: 'POST',
    body,
  });
}

/** Продлить пачку. Отказ по одному назначению не роняет остальные: он в `skipped`. */
export function extendAssignmentsBulk(body: BulkExtendIn): Promise<BulkExtendOut> {
  return request<BulkExtendOut>('/api/v1/assignments/extend/bulk', { method: 'POST', body });
}

/** Люди «на вахте» без действующего и будущего назначения; `siteId` — по объекту последнего. */
export function fetchOnShiftUnassigned(
  params: { siteId?: string | null } = {},
  signal?: AbortSignal,
): Promise<OnShiftUnassignedListOut> {
  const строка = queryString({ site_id: params.siteId ?? undefined });
  return request<OnShiftUnassignedListOut>(`/api/v1/assignments/on-shift-unassigned${строка}`, {
    signal,
  });
}

export function transferAssignment(
  assignmentId: string,
  body: TransferIn,
  idempotencyKey?: string,
): Promise<AssignmentOut> {
  return request<AssignmentOut>(
    `/api/v1/assignments/${encodeURIComponent(assignmentId)}/transfer`,
    { method: 'POST', body, idempotencyKey },
  );
}

export function changeRate(
  assignmentId: string,
  body: RateChangeIn,
  idempotencyKey?: string,
): Promise<RateChangeOut> {
  return request<RateChangeOut>(
    `/api/v1/assignments/${encodeURIComponent(assignmentId)}/rate-change`,
    { method: 'POST', body, idempotencyKey },
  );
}

export function listRateChanges(
  assignmentId: string,
  signal?: AbortSignal,
): Promise<ListResponse<RateChangeOut>> {
  return request<ListResponse<RateChangeOut>>(
    `/api/v1/assignments/${encodeURIComponent(assignmentId)}/rate-changes`,
    { signal },
  );
}

// ── Тарифы пачкой: рабочий лист «чего не хватает, чтобы месяц посчитался» ──────────────────
//
// Замер боевой базы 17.09.2026: назначений без суммы за период, по которым в июле-августе есть
// отработанные часы, — 247 у 154 человек на 46 объектах. Разбирать их по одному в карточках —
// 247 заходов, и заказчик их не сделает.

/** Назначение месяца, по которому начислять не из чего. */
export interface IncompleteRateOut {
  assignment_id: string;
  employee_id: string;
  employee_name: string;
  site_id: string;
  site_name: string;
  post_name: string;
  date_from: string;
  date_to?: string | null;
  rate_mode: string;
  rate_mode_title: string;
  period_amount?: number | null;
  norm_hours?: string | null;
  hourly_rate?: string | null;
  /**
   * Сколько вышло по табелю за этот месяц.
   *
   * Показывается, чтобы человек сам решил, какая у сотрудника норма. Выводить норму из факта
   * нельзя: сверка с восемнадцатью нормами, которые заказчик написал сам, дала совпадение в
   * семи случаях.
   */
  hours_fact?: string | null;
  /** Что стоит у того же человека на том же объекте. Предложение, а не подстановка. */
  suggested_period_amount?: number | null;
  suggested_norm_hours?: string | null;
  /** Чего именно не хватает, словами. */
  missing: string;
}

export interface IncompleteRateListOut {
  year: number;
  month: number;
  items: IncompleteRateOut[];
  total: number;
  employees: number;
  sites: number;
  summary: string;
}

export interface BulkRateItemIn {
  assignment_id: string;
  rate_mode?: string;
  period_amount?: number | null;
  norm_hours?: string | null;
  hourly_rate?: string | null;
  target_amount?: number | null;
  fact_hours_for_target?: string | null;
}

export interface BulkRateIn {
  effective_from: string;
  basis: string;
  items: BulkRateItemIn[];
}

export interface BulkRateSkipped {
  assignment_id: string;
  /** Кто и где: чтобы человек нашёл отклонённую строку глазами. */
  label: string;
  reason: string;
}

export interface BulkRateOut {
  applied: number;
  skipped: BulkRateSkipped[];
  summary: string;
}

export function fetchIncompleteRate(
  params: { year: number; month: number; siteId?: string | null },
  signal?: AbortSignal,
): Promise<IncompleteRateListOut> {
  const строка = queryString({
    year: params.year,
    month: params.month,
    site_id: params.siteId ?? undefined,
  });
  return request<IncompleteRateListOut>(`/api/v1/assignments/incomplete-rate${строка}`, { signal });
}

/**
 * Тарифы пачкой.
 *
 * Ключа операции нет: повтор с теми же числами запишет их ещё раз, и это правильно — история
 * тарифа хранит каждое изменение с датой и основанием, а не текущее значение.
 */
export function changeRateBulk(body: BulkRateIn): Promise<BulkRateOut> {
  return request<BulkRateOut>('/api/v1/assignments/rate-changes/bulk', { method: 'POST', body });
}
